<script setup lang="ts">
import { computed } from 'vue';
import BaseErrorState from '../ui/BaseErrorState.vue';
import { $t } from '@/composables/translations/useTranslate.ts';

interface Props {
  type?: 'no-results' | 'all-unavailable';
}

const props = withDefaults(defineProps<Props>(), {
  type: 'no-results',
});

const emit = defineEmits<{
  'back-to-search': [];
  'start-new-search': [];
}>();

const icon = computed(() => {
  return props.type === 'all-unavailable' ? 'icon-warning' : 'icon-info';
});

const iconClass = computed(() => {
  return props.type === 'all-unavailable' ? 'bg-error' : 'bg-gray-600';
});

const title = computed(() => {
  if (props.type === 'all-unavailable') {
    return $t('ibe.offer.fully-booked-title', 'Accommodation Fully Booked');
  } else {
    return $t('ibe.offer.no-results-title', 'No offers found');
  }
});

const message = computed(() => {
  if (props.type === 'all-unavailable') {
    return $t(
      'ibe.offer.fully-booked-message',
      'We apologize, but this accommodation is already fully booked for the selected dates. Please select a different period or accommodation above.'
    );
  } else {
    return $t(
      'ibe.offer.no-results-message',
      'Unfortunately, no offers are available for your search criteria. Please try different dates or modify your search.'
    );
  }
});

const secondaryButton = computed(() => {
  if (props.type === 'all-unavailable') {
    return {
      text: $t('ibe.offer.start-new-search', 'Start new search'),
      onClick: () => emit('start-new-search'),
    };
  }
  return undefined;
});

const primaryButton = computed(() => {
  if (props.type === 'all-unavailable') {
    return {
      text: $t('ibe.offer.back-to-results', 'Back to search results'),
      onClick: () => emit('back-to-search'),
    };
  }
  return undefined;
});
</script>

<template>
  <BaseErrorState
    :icon="icon"
    :icon-class="iconClass"
    :title="title"
    :message="message"
    :secondary-button="secondaryButton"
    :primary-button="primaryButton"
  />
</template>
